import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// IMPORTS DES COMPOSANTS MODULAIRES
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';
import { useAuth } from '../components/AuthContext';

const DocumentViewer: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string>('');
    const [documentName, setDocumentName] = useState<string>('');
    const [isDocx, setIsDocx] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || 'http://192.168.1.4:8000/api';

    useEffect(() => {
        if (!id) {
            setError('ID de document manquant');
            setLoading(false);
            return;
        }
        const isDocxFile = id.toLowerCase().endsWith('.docx');
        setIsDocx(isDocxFile);
        setDocumentName(id);
        setPreviewError(null);
        const encodedId = encodeURIComponent(id);
        if (!isDocxFile) {
            // PDF: charger en blob pour éviter X-Frame-Options: DENY sur iframe d’URL distante
            (async () => {
                try {
                    const pdfUrl = `${API_BASE}/documents/templates/${encodedId}/view/`;
                    const resp = await fetch(pdfUrl, { headers: { 'Accept': 'application/pdf' } });
                    if (!resp.ok) throw new Error(`Chargement PDF échoué (code ${resp.status})`);
                    const ct = (resp.headers.get('Content-Type') || '').toLowerCase();
                    if (!ct.includes('application/pdf')) throw new Error('Le document n\'est pas un PDF');
                    const blob = await resp.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    setDocumentUrl(blobUrl);
                } catch (e: any) {
                    setError(e.message || 'Impossible de charger le PDF');
                } finally {
                    setLoading(false);
                }
            })();
            return;
        }
        const previewUrl = `${API_BASE}/documents/templates/${encodedId}/preview/`;
        (async () => {
            try {
                const resp = await fetch(previewUrl, { headers: { 'Accept': 'application/pdf, text/html' } });
                if (!resp.ok) throw new Error(`Prévisualisation indisponible (code ${resp.status})`);
                const ct = (resp.headers.get('Content-Type') || '').toLowerCase();
                if (!(ct.includes('application/pdf') || ct.includes('text/html'))) {
                    throw new Error('Format inattendu de prévisualisation');
                }
                const blob = await resp.blob();
                const blobUrl = URL.createObjectURL(blob);
                setDocumentUrl(blobUrl);
            } catch (e: any) {
                setPreviewError(e.message || 'Erreur de prévisualisation');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, API_BASE]);

    const handleDownload = () => {
        const downloadUrl = `${API_BASE}/documents/templates/${encodeURIComponent(id || '')}/view/`;
        window.open(downloadUrl, '_blank');
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (!user) {
        navigate('/login', { replace: true });
        return null;
    }

    return (
        <div className={`flex min-h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
            <Sidebar />

            <main className="flex-1 p-6 overflow-y-auto">
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />

                <div className="max-w-7xl mx-auto">
                    {/* En-tête */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <button onClick={handleBack} className="btn btn-circle btn-ghost">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold">Visualisation du document</h1>
                                <p className="text-base-content/60">{documentName}</p>
                            </div>
                        </div>
                        <button onClick={handleDownload} className="btn btn-primary gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Télécharger
                        </button>
                    </div>

                    {/* Contenu */}
                    <div className="card bg-base-100 shadow-xl">
                        <div className="card-body p-0">
                            {loading ? (
                                <div className="flex items-center justify-center h-96">
                                    <span className="loading loading-spinner loading-lg"></span>
                                </div>
                            ) : error ? (
                                <div className="alert alert-error">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            ) : isDocx ? (
                                previewError ? (
                                    <div className="flex flex-col items-center justify-center p-12 space-y-6">
                                        <div className="alert alert-warning max-w-xl">
                                            <span>Prévisualisation PDF indisponible : {previewError}</span>
                                        </div>
                                        <p className="text-base-content/70 max-w-md text-center">
                                            Téléchargez le document Word pour le consulter dans un logiciel compatible.
                                        </p>
                                        <div className="flex gap-3">
                                            <button onClick={handleDownload} className="btn btn-primary btn-lg gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                Télécharger le DOCX
                                            </button>
                                            <button onClick={handleBack} className="btn btn-outline btn-lg">Retour</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-screen">
                                        <iframe
                                            src={documentUrl}
                                            className="w-full h-full border-0"
                                            title={documentName}
                                        />
                                    </div>
                                )
                            ) : (
                                <div className="w-full h-screen">
                                    <iframe
                                        src={documentUrl}
                                        className="w-full h-full border-0"
                                        title={documentName}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <NotificationPanel 
                isOpen={isNotifPanelOpen} 
                onClose={() => setIsNotifPanelOpen(false)}
            />
        </div>
    );
};

export default DocumentViewer;
